import { CalendarClock, SlidersHorizontal, X } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCustomerClassifications } from '@/api/queries/customer-classifications-queries'
import { useJobGroupClusters } from '@/api/queries/job-group-cluster-queries'
import { useJobGroups } from '@/api/queries/job-group-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { CommitmentMatrixPeriodFilter } from '@/components/administration/commitment-matrix/commitment-matrix-period-filter'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useDeviceType } from '@/hooks/useDeviceType'
import type { KpiMetricsFilterState } from '@/store/kpi-metrics-filter-store'
import { useKpiMetricsFilterStore } from '@/store/kpi-metrics-filter-store'

/** Radix Select não aceita value vazio, então "todas" usa um sentinel */
const ALL_CLASSIFICATIONS = 'all'
const WEEK_OPTIONS = Array.from({ length: 53 }, (_, i) => i + 1)

interface KpiMetricsFilterBarProps {
	value: KpiMetricsFilterState
	onChange: (value: KpiMetricsFilterState) => void
	/** A classificação do cliente só se aplica ao dashboard de Ver e Agir */
	showCustomerClassification?: boolean
}

function countActiveFilters(
	value: KpiMetricsFilterState,
	showCustomerClassification: boolean,
): number {
	let count = 0
	if (value.weekOfYear != null) count++
	if (value.branchIds.length > 0) count++
	if (value.crIds.length > 0) count++
	if (value.jobGroupClusterId) count++
	if (value.jobGroupId) count++
	if (value.personName) count++
	if (showCustomerClassification && value.customerClassificationId) count++
	return count
}

export function KpiMetricsFilterBar({
	value,
	onChange,
	showCustomerClassification = false,
}: KpiMetricsFilterBarProps) {
	const { t } = useTranslation()
	const { isMobile } = useDeviceType()
	const activeCount = countActiveFilters(value, showCustomerClassification)

	if (isMobile) {
		return (
			<Drawer direction="bottom">
				<DrawerTrigger asChild>
					<Button
						type="button"
						variant="outline"
						className="h-9 w-full justify-between gap-2 border-kaefer-light-silver bg-muted/20"
					>
						<span className="flex items-center gap-2 text-sm font-medium text-kaefer-dark-grey">
							<SlidersHorizontal className="size-4" />
							{t('kpiMetrics.filters.title')}
						</span>
						{activeCount > 0 && (
							<Badge className="h-5 min-w-5 justify-center rounded-full bg-kaefer-medium-grey px-1.5 text-[11px] tabular-nums">
								{activeCount}
							</Badge>
						)}
					</Button>
				</DrawerTrigger>
				<DrawerContent className="max-h-[85vh]">
					<DrawerHeader>
						<DrawerTitle>{t('kpiMetrics.filters.title')}</DrawerTitle>
					</DrawerHeader>
					<div className="flex flex-col gap-4 overflow-y-auto px-4 pb-2">
						<FilterFields
							value={value}
							onChange={onChange}
							showCustomerClassification={showCustomerClassification}
						/>
					</div>
					<DrawerFooter className="flex-row gap-2">
						<DrawerClose asChild>
							<Button type="button" variant="cancel" className="flex-1">
								{t('kpiMetrics.filters.done')}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</DrawerContent>
			</Drawer>
		)
	}

	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-muted/20 p-3">
			<FilterFields
				value={value}
				onChange={onChange}
				showCustomerClassification={showCustomerClassification}
			/>
		</div>
	)
}

function FilterFields({
	value,
	onChange,
	showCustomerClassification = false,
}: KpiMetricsFilterBarProps) {
	const { t } = useTranslation()
	const resetFilter = useKpiMetricsFilterStore((s) => s.resetFilter)
	const { data: branches } = useOrganization()
	const { data: jobGroupClusters } = useJobGroupClusters()
	const { data: jobGroups } = useJobGroups()
	const { data: customerClassifications } = useCustomerClassifications(
		{ page: 1, limit: 100 },
		{ enabled: showCustomerClassification },
	)

	const branchOptions = useMemo(
		() => (branches ?? []).map((b) => ({ value: b.id, label: b.name })),
		[branches],
	)
	const crOptions = useMemo(() => {
		const filteredBranches =
			value.branchIds.length > 0
				? (branches ?? []).filter((b) => value.branchIds.includes(b.id))
				: (branches ?? [])
		return filteredBranches.flatMap((b) =>
			b.crs.map((c) => ({ value: c.id, label: c.name })),
		)
	}, [branches, value.branchIds])
	// Escolher filiais restringe as opções de CR; trocar as filiais tira da seleção os CRs que saíram do escopo
	const handleBranchesChange = (branchIds: string[]) => {
		const allowedCrIds = new Set(
			(branchIds.length > 0
				? (branches ?? []).filter((b) => branchIds.includes(b.id))
				: (branches ?? [])
			).flatMap((b) => b.crs.map((c) => c.id)),
		)
		onChange({
			...value,
			branchIds,
			crIds: value.crIds.filter((id) => allowedCrIds.has(id)),
		})
	}

	const jobGroupClusterOptions = useMemo(
		() => (jobGroupClusters ?? []).map((c) => ({ value: c.id, label: c.name })),
		[jobGroupClusters],
	)
	const jobGroupOptions = useMemo(
		() => (jobGroups ?? []).map((g) => ({ value: g.id, label: g.name })),
		[jobGroups],
	)

	const customerClassificationOptions = useMemo(
		() =>
			(customerClassifications?.items ?? []).map((c) => ({
				value: c.id,
				label: c.name,
			})),
		[customerClassifications],
	)

	const hasWeekFilter = value.weekOfYear != null
	const hasActiveFilters =
		countActiveFilters(value, showCustomerClassification) > 0

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start">
				<div className="flex flex-col gap-1.5">
					<span className="text-[11px] font-semibold text-kaefer-medium-grey">
						{t('kpiMetrics.filters.period')}
					</span>
					<CommitmentMatrixPeriodFilter
						value={value.period}
						onChange={(period) =>
							onChange({ ...value, period, weekOfYear: null, year: null })
						}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<span className="flex items-center gap-1 text-[11px] font-semibold text-kaefer-medium-grey">
						<CalendarClock className="size-3" />
						{t('kpiMetrics.filters.weekOfYear')}
					</span>
					<div className="flex items-center gap-1.5">
						<Select
							value={value.weekOfYear ? String(value.weekOfYear) : ''}
							onValueChange={(week) =>
								onChange({
									...value,
									weekOfYear: Number(week),
									year: value.year ?? new Date().getFullYear(),
								})
							}
						>
							<SelectTrigger className="h-9 flex-1 sm:h-8 sm:w-24 sm:flex-none">
								<SelectValue placeholder={t('kpiMetrics.filters.week')} />
							</SelectTrigger>
							<SelectContent>
								{WEEK_OPTIONS.map((week) => (
									<SelectItem key={week} value={String(week)}>
										{week}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Input
							type="number"
							placeholder={t('kpiMetrics.filters.year')}
							value={value.year ?? ''}
							onChange={(e) =>
								onChange({
									...value,
									year: e.target.value ? Number(e.target.value) : null,
								})
							}
							className="h-9 w-20 shrink-0 sm:h-8"
							disabled={!hasWeekFilter}
						/>
						{hasWeekFilter && (
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-9 shrink-0 sm:size-8"
								onClick={() =>
									onChange({ ...value, weekOfYear: null, year: null })
								}
							>
								<X className="size-3.5" />
							</Button>
						)}
					</div>
				</div>

				<div className="flex flex-col gap-1.5 sm:w-56">
					<span className="text-[11px] font-semibold text-kaefer-medium-grey">
						{t('kpiMetrics.filters.branches')}
					</span>
					<MultiSelect
						options={branchOptions}
						values={value.branchIds}
						onValuesChange={handleBranchesChange}
						placeholder={t('kpiMetrics.filters.allBranches')}
						searchPlaceholder={t('kpiMetrics.filters.search')}
						emptyMessage={t('kpiMetrics.filters.noResults')}
						className="h-9 w-full sm:h-8"
					/>
				</div>

				<div className="flex flex-col gap-1.5 sm:w-64">
					<span className="text-[11px] font-semibold text-kaefer-medium-grey">
						{t('kpiMetrics.filters.crs')}
					</span>
					<MultiSelect
						options={crOptions}
						values={value.crIds}
						onValuesChange={(crIds) => onChange({ ...value, crIds })}
						placeholder={t('kpiMetrics.filters.allCrs')}
						searchPlaceholder={t('kpiMetrics.filters.search')}
						emptyMessage={t('kpiMetrics.filters.noResults')}
						className="h-9 w-full sm:h-8"
					/>
				</div>

				<div className="flex flex-col gap-1.5 sm:w-48">
					<span className="text-[11px] font-semibold text-kaefer-medium-grey">
						{t('kpiMetrics.filters.jobGroupCluster')}
					</span>
					<SearchableSelect
						options={jobGroupClusterOptions}
						value={value.jobGroupClusterId ?? ''}
						onValueChange={(id) =>
							onChange({
								...value,
								jobGroupClusterId: id || null,
								jobGroupId: id ? null : value.jobGroupId,
							})
						}
						placeholder={t('kpiMetrics.filters.allClusters')}
						searchPlaceholder={t('kpiMetrics.filters.search')}
						className="h-9 w-full sm:h-8"
					/>
				</div>

				<div className="flex flex-col gap-1.5 sm:w-48">
					<span className="text-[11px] font-semibold text-kaefer-medium-grey">
						{t('kpiMetrics.filters.jobGroup')}
					</span>
					<SearchableSelect
						options={jobGroupOptions}
						value={value.jobGroupId ?? ''}
						onValueChange={(id) =>
							onChange({ ...value, jobGroupId: id || null })
						}
						placeholder={t('kpiMetrics.filters.allJobGroups')}
						searchPlaceholder={t('kpiMetrics.filters.search')}
						disabled={!!value.jobGroupClusterId}
						className="h-9 w-full sm:h-8"
					/>
				</div>

				<div className="flex flex-col gap-1.5 sm:w-48">
					<span className="text-[11px] font-semibold text-kaefer-medium-grey">
						{t('kpiMetrics.filters.personName')}
					</span>
					<Input
						value={value.personName}
						onChange={(e) => onChange({ ...value, personName: e.target.value })}
						placeholder={t('kpiMetrics.filters.personNamePlaceholder')}
						className="h-9 w-full sm:h-8"
					/>
				</div>

				{showCustomerClassification && (
					<div className="flex flex-col gap-1.5 sm:w-48">
						<span className="text-[11px] font-semibold text-kaefer-medium-grey">
							{t('kpiMetrics.filters.customerClassification')}
						</span>
						<Select
							value={value.customerClassificationId ?? ALL_CLASSIFICATIONS}
							onValueChange={(id) =>
								onChange({
									...value,
									customerClassificationId:
										id === ALL_CLASSIFICATIONS ? null : id,
								})
							}
						>
							<SelectTrigger className="h-9 w-full sm:h-8">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL_CLASSIFICATIONS}>
									{t('kpiMetrics.filters.allCustomerClassifications')}
								</SelectItem>
								{customerClassificationOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}

				{hasActiveFilters && (
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-9 gap-1 text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-dark-grey sm:mt-auto sm:h-8"
						onClick={() => resetFilter()}
					>
						<X className="size-3.5" />
						{t('kpiMetrics.filters.clear')}
					</Button>
				)}
			</div>
		</div>
	)
}
